'use client';

import React, { useEffect, useRef } from 'react';
import { ReactionPickerState } from './types';

const EMOJI_LIST = ['❤️', '👍', '😂', '😮', '😢', '🔥', '🙌', '👏', '🎉', '💯'];

interface ReactionPickerProps {
  state: ReactionPickerState;
  onReact: (emoji: string) => void;
  onClose: () => void;
}

export default function ReactionPicker({ state, onReact, onClose }: ReactionPickerProps) {
  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) onClose();
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  const pickerWidth = 280;
  const clampedX = Math.min(state.x - pickerWidth / 2, (typeof window !== 'undefined' ? window.innerWidth : 400) - pickerWidth - 8);
  const clampedY = state.y - 60;

  return (
    <div
      ref={pickerRef}
      className="fixed z-50 context-menu-surface rounded-2xl p-2 animate-scale-in"
      style={{ left: `${Math.max(8, clampedX)}px`, top: `${Math.max(8, clampedY)}px`, width: `${pickerWidth}px` }}
      role="dialog"
      aria-label="Choose a reaction"
    >
      <div className="flex items-center justify-center gap-1 flex-wrap">
        {EMOJI_LIST.map((emoji) => (
          <button
            key={`emoji-${emoji}`}
            onClick={() => onReact(emoji)}
            className="w-10 h-10 flex items-center justify-center text-[22px] rounded-xl hover:bg-primary/10 transition-all duration-150 active:scale-90 hover:scale-110"
            aria-label={`React with ${emoji}`}
            title={emoji}
          >
            {emoji}
          </button>
        ))}
      </div>
    </div>
  );
}